import ShipCostFormulas from './formulas/ship-cost-formulas.js';
import shared from './shared.js';

// ShipCollection is a container for ships
// The Fleet class (backend) inherits from the ShipCollection (shared)
// The ShipCollection automatically calculates stats
// Naming: A fleet/ship_collection consists of ships, which have a ship_design
//
// Usages:
// - Stationed ships on a planet are a ShipCollection
// - Each fleet on the backend is a Fleet, which inherits from ShipCollection
// - A fleet on the client side is a ShipCollection
//
// Layout (3 ships of design 28 took 88 damage each):
// this.ships = [{design: {id: 28, name: "Zerg", owner_id: 70, owner_name: "net", stats: {structure: 2, ...}, config: {techs}}, count: 123, sustained_damage: [{damage: 88, count: 3}]},
//               {design: {id: 8128, name: "Queen", owner_id: 70, owner_name: "net", stats: {structure: 23, ...}, config: {techs}}, count: 321}]
export default class ShipCollection {
    constructor(data) {
        this.ship_count = 0;
        this.ships = [];

        // "copy ctor"
        if (data) {
            Object.assign(this, JSON.parse(JSON.stringify(data)));
        }

        this._refreshStats();
    }

    empty() {
        return this.ship_count === 0;
    }

    forEach(callback) {
        this.ships.forEach(callback);
    }

    map(callback) {
        return this.ships.map(callback);
    }

    getFleetThreat() {
        return this.stats.thread;
    }

    hasShips(ship_design_id, count) {
        for (let i = 0; i < this.ships.length; i++) {
            if (this.ships[i].design.id === ship_design_id) {
                return this.ships[i].count >= count;
            }
        }

        return count === 0;
    }

    hasShipCollection(other) {
        for (let i = 0; i < this.ships.length; i++) {
            if (this.ships[i].count !== other.getShips(this.ships[i].design.id).count) {
                return false;
            }
        }
        return true;
    }

    hasSameShips(other) {
        return this.hasShipCollection(other) && other.hasShipCollection(this);
    }

    getShips(ship_design_id) {
        for (let i = 0; i < this.ships.length; i++) {
            if (this.ships[i].design.id === ship_design_id) {
                return this.ships[i];
            }
        }
    }

    getStructureForShipType(ship_design) {
        return ship_design.count * ship_design.design.stats.structure;
    }

    getSustainedDamageForShipType(ship_design) {
        return ship_design.sustained_damage.reduce((acc, iter) => acc + iter.damage * iter.count, 0);
    }

    getDamageRatioForShipType(ship_design) {
        let structure = this.getStructureForShipType(ship_design);
        return (structure - this.getSustainedDamageForShipType(ship_design)) / structure;
    }

    _getShipsAtIndex(rnd_ship_idx_with_thread) {
        for (let ship_type_idx = 0; ship_type_idx < this.ships.length; ship_type_idx++) {
            const ships = this.ships[ship_type_idx];
            const ships_thread = ships.count * ships.design.stats.thread;
            if (rnd_ship_idx_with_thread < ships_thread) {
                return [ship_type_idx, Math.floor(rnd_ship_idx_with_thread / ships.design.stats.thread)];
            }
            rnd_ship_idx_with_thread -= ships_thread;
        }
    }

    // In the example above, 125 would damage {design: {id: 8128, ...stats}, count: 321}
    // Damage specified ship and, if a ship is lost, it is returned
    dealDamage(rnd_ship_idx_with_thread, damage) {
        let total_damage = damage;
        shared.assert(rnd_ship_idx_with_thread < this.getFleetThreat());

        // Find ship type
        let [ship_idx, rnd_ship_idx] = this._getShipsAtIndex(rnd_ship_idx_with_thread);
        const ships = this.ships[ship_idx];

        // Remove ship from current damage bucket (if the rnd_idx is in the 0 damage bucket -> nothing to do)
        for (let damage_idx = 0; damage_idx < ships.sustained_damage.length; damage_idx++) {
            const sustained_damage = ships.sustained_damage[damage_idx];
            if (rnd_ship_idx < sustained_damage.count) {
                total_damage += ships.sustained_damage[damage_idx].damage;
                ships.sustained_damage[damage_idx].count -= 1;
                if (ships.sustained_damage[damage_idx].count === 0) {
                    ships.sustained_damage.splice(damage_idx, 1);
                }
                break;
            }
            rnd_ship_idx -= sustained_damage.count;
        }

        // Re-add the now more damaged ship, or remove it completely if its dead
        if (ships.design.stats.structure <= total_damage) {
            // Ship was lost -> Remove ship
            this.removeShips(ships.design, 1);
            const overkill_damage = total_damage - ships.design.stats.structure;
            const effective_damage = damage - overkill_damage;
            return [ships.design.id, true, effective_damage, overkill_damage];
        } else {
            // Ship was only damaged -> Add damaged ship
            let bucket = ships.sustained_damage.find(sustained_damage => sustained_damage.damage === total_damage);
            if (bucket) {
                bucket.count += 1;
            } else {
                ships.sustained_damage.push({ damage: total_damage, count: 1 });
            }
            this._refreshStats();
            return [ships.design.id, false, damage, 0];
        }
    }

    getShipCount(ship_design_id) {
        let ships = this.getShips(ship_design_id);
        return ships ? ships.count : 0;
    }

    addShipCollection(other) {
        for (let i = 0; i < other.ships.length; i++) {
            this.addShips(other.ships[i].design, other.ships[i].count);
        }
        return this;
    }

    addShips(ship_design, count) {
        let current_count = this.getShipCount(ship_design.id);
        this.setShipCount(ship_design, current_count + count);
        return this;
    }

    removeShipCollection(other) {
        for (let i = 0; i < other.ships.length; i++) {
            this.removeShips(other.ships[i].design, other.ships[i].count);
        }
        return this;
    }

    removeShips(ship_design, count) {
        let current_count = this.getShipCount(ship_design.id);
        this.setShipCount(ship_design, current_count - count);
        return this;
    }

    removeAllShips() {
        this.ships = [];
        this.ship_count = 0;
        this._refreshStats();
        return this;
    }

    setShipCount(ship_design, count) {
        shared.assert(ship_design);
        if (count <= 0) {
            count = 0;
        }

        // Ship design is already in fleet
        for (let i = 0; i < this.ships.length; i++) {
            if (this.ships[i].design.id === ship_design.id) {
                if (count > 0) {
                    this.ships[i].count = count;
                } else {
                    this.ships.splice(i, 1);
                }
                this._refreshStats();
                return this;
            }
        }

        // Otherwise, add the ship design (immutable -> it's fine not to copy)
        this.ships.push({
            design: ship_design,
            count: count,
            sustained_damage: [],
        });
        this._refreshStats();
        return this;
    }

    _refreshStats() {
        this.ship_count = this.ships.reduce((sum, ship) => {
            return sum + ship.count
        }, 0);

        this.stats = {
            attack: 0,
            structure: 0, // Structure if fleet would be 100% repaired.
            armor: 0,
            shield: 0,
            cargo: 0,
            thrust: 0,
            repair: 0,
            acceleration: 0,
            thread: 0,
            sustained_damage: 0,
        }

        this.ships.sort((lhs, rhs) => lhs.design.id - rhs.design.id);
        this.ships.forEach((ship) => {
            this.stats.attack += ship.design.stats.attack * ship.count;
            this.stats.structure += ship.design.stats.structure * ship.count;
            this.stats.armor += ship.design.stats.armor * ship.count;
            this.stats.shield += ship.design.stats.shield * ship.count;
            this.stats.cargo += ship.design.stats.cargo * ship.count;
            this.stats.thrust += ship.design.stats.thrust * ship.count;
            this.stats.repair += ship.design.stats.repair * ship.count;
            this.stats.thread += ship.design.stats.thread * ship.count;
            this.stats.sustained_damage += ship.sustained_damage.reduce((acc, iter) => acc + iter.damage * iter.count, 0);
        });
        this.stats.acceleration = this._getAcceleration();
    }

    _getAcceleration() {
        if (this.ship_count === 0) {
            return 0;
        }

        let worst_acceleration = null;
        this.ships.forEach((ship) => {
            let acceleration = ShipCostFormulas.getAcceleration(ship.design.stats);
            if (worst_acceleration === null || acceleration < worst_acceleration) {
                worst_acceleration = acceleration;
            }
        });

        return worst_acceleration;
    }

    repair() {
        this.ships.forEach(ship => ship.sustained_damage = []);
        this._refreshStats();
    }
}